import type { Dispatch, ReactNode, SetStateAction } from 'react';
import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import type {
  FilterFormValues, TCandidateOrigin, TMatcherType, TReference,
} from '@vlabs/monokit/entities/matcher/types/types';
import { toast } from 'react-toastify';

import { buildMatchPayload } from '../../api/buildMatchPayload';
import { mapMatchResults } from '../../api/mapMatchResults';
import { useSearchApi } from '../../api/SearchApiProvider';
import { useSearchPageTranslation } from '../../i18n';
import type { TTransformedSearchResult } from '../../model/types';

interface SearchProviderState {
  matcherType: TMatcherType;
  setMatcherType: (matcherType: TMatcherType) => void;
  candidateOrigin: TCandidateOrigin;
  setCandidateOrigin: (candidateOrigin: TCandidateOrigin) => void;
  references: TReference[];
  setReferences: (references: TReference[]) => void;
  searchResults: TTransformedSearchResult[] | undefined;
  setSearchResults: Dispatch<SetStateAction<TTransformedSearchResult[] | undefined>>;
  search: (filters: FilterFormValues) => void;
  resetSearchState: () => void;
  onMatcherTypeChange: (matcherType: TMatcherType) => void;
}

const SearchContext = createContext<SearchProviderState | null>(null);

interface SearchProviderProps {
  canSearch?: () => boolean;
  children: ReactNode;
}

function SearchProvider({
  canSearch = () => true,
  children,
}: SearchProviderProps): JSX.Element {
  const api = useSearchApi();
  const { t } = useSearchPageTranslation();
  const [searchResults, setSearchResults] = useState<TTransformedSearchResult[] | undefined>(undefined);
  const [references, setReferences] = useState<TReference[]>([]);
  const [candidateOrigin, setCandidateOrigin] = useState<TCandidateOrigin>('events');
  const [matcherType, setMatcherType] = useState<TMatcherType>('face');

  const resetSearchState = useCallback(() => {
    setSearchResults(undefined);
  }, []);

  const search = useCallback(async (filters: FilterFormValues) => {
    if (!canSearch()) return;
    if (!(references.length > 0)) {
      toast.warning(t('page.hint.choose_search_object'));
      return;
    }

    if (candidateOrigin === 'faces' && !filters.list_id) {
      toast.warning(t('page.hint.choose_list'));
      return;
    }

    try {
      const payload = buildMatchPayload(filters, references, candidateOrigin);
      const response = matcherType === 'body'
        ? await api.matchBodies(payload)
        : await api.matchFaces(payload);
      setSearchResults(mapMatchResults(response, filters.order));
    } catch (error) {
      console.error(error);
      toast.error(t('page.error.search_failed'));
    }
  }, [api, canSearch, references, matcherType, candidateOrigin, t]);

  const onMatcherTypeChange = useCallback((type: TMatcherType) => {
    if (type === 'body') setCandidateOrigin('events');
    setMatcherType(type);
    setSearchResults(undefined);
  }, []);

  const context = useMemo(() => ({
    matcherType,
    setMatcherType,
    candidateOrigin,
    setCandidateOrigin,
    references,
    setReferences,
    searchResults,
    setSearchResults,
    search,
    resetSearchState,
    onMatcherTypeChange,
  }), [
    searchResults,
    setSearchResults,
    matcherType,
    setMatcherType,
    candidateOrigin,
    setCandidateOrigin,
    references,
    setReferences,
    search,
    onMatcherTypeChange,
    resetSearchState,
  ]);

  return (
    <SearchContext.Provider value={context}>
      {children}
    </SearchContext.Provider>
  );
}

function useSearch(): SearchProviderState {
  const context = useContext(SearchContext);
  if (!context) throw Error('No context SearchProvider');
  return context;
}

export { SearchProvider, useSearch };
